// Main Users Component
import React, { Fragment, useState, useEffect } from "react";
import {
  Button,
  Card,
  Row,
  Col,
  Spinner,
  Tab,
  Tabs,
} from "react-bootstrap";
import { useCookies } from "react-cookie";
import { useNavigate, useParams } from "react-router-dom";
import {
  modifyUserGroup,
  getSingleUserGroup,
  getUsers,
  fetchLiveClasses,
} from "ServerAPI/serverAPI";
import CommonHeader from "layouts/dashboard/CommonHeader";
import userGroupIcon from "../../assets/images/Users.svg";
import { formatDate } from "helper/utils";
import { CircularProgress, TextField } from "@mui/material";
import Select from "react-select";
import Multiselect from "multiselect-react-dropdown";
import { useToast } from "context/toast-context/ToastProvider";
import CommonDialog from "components/Dialog/CommonDialog";
import UpdateUserGroup from "./UpdateUserGroup";
import { FEATURES, useFeaturePermissions } from "constants/FeaturePermissions";
import SearchIcon from "@mui/icons-material/Search";
// Import the new sub-components
import UserGroupTable from "./UserGroupTable";
import CoursesGroupTable from "./CoursesGroupTable";
import LiveClassesGroupTable from "./LiveClassesGroupTable";
import CreateMeetingModal from "./CreateMeetingModal";
import SummaryCards from "./SummaryCards";

// Icons for summary cards
import { PersonFill, Calendar3, BookFill, CalendarEvent, PersonBadgeFill, BuildingFill } from "react-bootstrap-icons";

const Users = () => {
  const [cookies] = useCookies();
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasFeature } = useFeaturePermissions();

  // Main data states
  const [groupName, setGroupName] = useState("");
  const [courseNames, setCourseNames] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [userDetails, setUserDetails] = useState({});
  const [coordinator, setCoordinator] = useState("");
  const [classes, setClasses] = useState([]);

  // User management states
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [addingUsers, setAddingUsers] = useState(false);
  const [organizers, setOrganizers] = useState([]);
  const [existingUsers, setExistingUsers] = useState([]);

  // Search states
  const [searchTerm, setSearchTerm] = useState("");
  const [courseSearchTerm, setCourseSearchTerm] = useState("");
  const [liveClassSearchTerm, setLiveClassSearchTerm] = useState("");

  // Modal states
  const [showMeetingModel, setShowMeetingModel] = useState(false);
  const [updateUserGroupModal, setUpdateUserGroupModal] = useState(false);
  const [clickedUserGroup, setClickedUserGroup] = useState({
    name: "",
    startDate: "",
    endDate: "",
  });
  const [updateOperationLoading, setUpdateOperationLoading] = useState(false);

  // Tab state for better organization
  const [activeTab, setActiveTab] = useState("users");

  // Extract ISO string from MongoDB date object
  const getISODateString = (mongoDateObject) => {
    return mongoDateObject?.$date || null;
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await getUsers(
        cookies.email,
        cookies.roleId,
        cookies.organizationId
      );
      const users = response.data.users || [];
      const existingUserIds = new Set(data.map((user) => user.userId));

      const filteredUsers = users
        .filter(
          (user) =>
            user.role &&
            !existingUserIds.has(user.userId) &&
            user.role.roleId === "courseParticipant"
        )
        .map((user) => ({
          value: user.userId,
          label: user.name,
        }));

      const organizers = users
        .filter((user) => user?.role?.roleId !== "courseParticipant")
        .map((user) => ({
          value: user.userId,
          label: user.name,
        }));

      const usersWithoutLoggedInUser = users.filter((user) => {
        return user?.userId !== cookies?.userId;
      });

      setExistingUsers(
        usersWithoutLoggedInUser.map((user) => {
          return {
            value: user?.userId,
            label: user?.name,
          };
        })
      );
      setOrganizers(organizers);
      setAvailableUsers(filteredUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchUsers = async (courseId = "") => {
    setLoading(true);

    try {
      const email = cookies.email;
      const userId = cookies.userId;
      const response = await getSingleUserGroup(email, id, userId);

      const userGroupFromApi = response.data.data.userGroup;

      setGroupName(userGroupFromApi.userGroupName);
      setStartDate(formatDate(userGroupFromApi?.startDate?.$date));
      setEndDate(formatDate(userGroupFromApi?.endDate?.$date));
      setCoordinator(userGroupFromApi.coordinatorName);

      const transformedUserDetails = {
        ...userGroupFromApi,
        startDate: getISODateString(userGroupFromApi.startDate),
        endDate: getISODateString(userGroupFromApi.endDate),
        name: userGroupFromApi.userGroupName,
      };
      setUserDetails(transformedUserDetails);

      const courseData = response.data.data?.courses?.map((course, index) => ({
        id: index + 1,
        ...course,
      }));
      setCourseNames(courseData);

      const formattedData = response.data.data.users.map((user, index) => ({
        id: index + 1,
        name: user.name,
        email: user.email,
        phoneNumber: user.phoneNumber,
        coursePercentage: user.coursePercentage,
        quizResult: user.quizResult,
        attemptCount: user.attemptCount,
        userId: user.userId,
      }));

      setData(formattedData);
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle search functionality user
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Filter data based on search user
  const filteredData = data.filter((user) => {
    return (
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Handle search functionality Courses
  const handleCourseSearchChange = (event) => {
    setCourseSearchTerm(event.target.value);
  };

  // Filter data based on search Courses
  const filteredCourses = courseNames.filter(course =>
    course.courseTitle.toLowerCase().includes(courseSearchTerm.toLowerCase())
  );

  //  Filter data based on search Live class
const handleLiveClassSearchChange = (event) => {
  setLiveClassSearchTerm(event.target.value);
};


  const fetchAllLiveClasses = async () => {
    try {
      const payload = {
        userGroupId: id,
      };
      const response = await fetchLiveClasses("", payload);
      if (response.status === 200) {
        const classesWithId = response?.data?.classes?.map((cls, index) => ({
          id: index + 1,
          ...cls,
        })) || [];
        setClasses(classesWithId);
      } else {
        setClasses([]);
      }
    } catch (error) {
      showToast("Error in fetching live classes", "error");
    }
  };

  const handleAddUsers = async () => {
    if (selectedUsers?.length === 0) return;

    setAddingUsers(true);

    const selectedUserDetails = selectedUsers.map((user) =>
      availableUsers.find((u) => u.value === user.value)
    );

    const validUsers = selectedUserDetails.filter(
      (user) => user?.assignedCourseIds !== null
    );

    if (validUsers?.length === 0) {
      console.error("No valid users to add");
      setAddingUsers(false);
      return;
    }

    const userIds = selectedUsers.map((user) => user.value);

    const payload = {
      userGroupId: id,
      operation: "add",
      userIds: userIds,
      authorUserId: cookies.userId,
    };

    try {
      const response = await modifyUserGroup(payload);
      await fetchUsers();
      setSelectedUsers([]);
      showToast("Users added successfully", "success");
    } catch (error) {
      console.error("Error adding users:", error);
      showToast("Failed to add users", "error");
    } finally {
      setAddingUsers(false);
    }
  };

  // Update User Group handlers
  const handleUpdateClick = (userGroup) => {
    setClickedUserGroup({
      ...userGroup,
      name: userGroup.userGroupName,
    });
    setUpdateUserGroupModal(true);
  };

  const handleChangeForUpdateUser = (e) => {
    const { name, value } = e.target;
    setClickedUserGroup((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateUpdateUserGroupsFields = () => {
    return !!(clickedUserGroup?.name && clickedUserGroup?.startDate && clickedUserGroup?.endDate);
  };

  const updateUserModalCloseHandler = () => {
    setUpdateUserGroupModal(false);
    setUpdateOperationLoading(false);
  };

  const updateUserGroupData = async () => {
    setUpdateOperationLoading(true);
    if (!validateUpdateUserGroupsFields()) {
      showToast("Each field is required", "error");
      setUpdateOperationLoading(false);
      return;
    }

    const payload = {
      name: clickedUserGroup.name,
      startDate: new Date(clickedUserGroup.startDate),
      endDate: new Date(clickedUserGroup.endDate),
      userGroupId: clickedUserGroup.userGroupId,
      courseIds: clickedUserGroup.courseIds,
      userIds: clickedUserGroup.userIds,
    };

    try {
      const response = await modifyUserGroup(payload);
      if (response?.data?.status) {
        showToast("User group updated", "success");
        fetchUsers();
        updateUserModalCloseHandler();
      } else {
        showToast(response?.data?.message || "User group update failed.", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "An error occurred while updating the user group.",
        "error"
      );
    } finally {
      setUpdateOperationLoading(false);
    }
  };



  useEffect(() => {
    fetchUsers();
    fetchAllLiveClasses();
  }, [cookies, id]);

  useEffect(() => {
    fetchAvailableUsers();
  }, [data]);



  // Prepare summary data for cards
  const summaryData = [
    {
      title: "Users",
      value: String(data?.length).padStart(2, "0"),
      icon: <PersonFill size={20} />,
      color: "#007bff"
    },
    {
      title: "Courses",
      value: userDetails?.courseTitles?.length || 0,
      icon: <BookFill size={20} />,
      color: "#17a2b8"
    },
    {
      title: "Start Date",
      value: startDate,
      icon: <Calendar3 size={20} />,
      color: "#28a745"
    },
    {
      title: "End Date",
      value: endDate,
      icon: <CalendarEvent size={20} />,
      color: "#ffc107"
    },
    {
      title: "Coordinator",
      value: coordinator,
      icon: <PersonBadgeFill size={20} />,
      color: "#6f42c1"
    },
    {
      title: "Department",
      value: userDetails?.department || "N/A",
      icon: <BuildingFill size={20} />,
      color: "#fd7e14"
    }
  ];

  return (
    <Fragment>
      <Row
        style={{
          background:
            "linear-gradient(93.26deg, #224B99 -10.17%, #3A4A6E 151.98%)",
        }}
      >
        <CommonHeader
          text={groupName || ""}
          subText="Explore the details of this training program, including its
                members and their progress."
          icon={userGroupIcon}
        />
      </Row>

      {loading ? (
        <div className="d-flex justify-content-center mt-5">
          <CircularProgress />
        </div>
      ) : (
        <Row>
          <Col sm={10} className="mx-auto px-2 pt-5">
            <Card>
              <Card.Body className="p-4">
                {/* Top Summary Cards */}
                <SummaryCards
                  summaryData={summaryData}
                  onEditClick={() => handleUpdateClick({
                    ...userDetails,
                    name: userDetails.userGroupName,
                    userIds: data.map(user => user.userId),
                  })}
                />


                {/* Tabbed Tables */}
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="mb-3"
                  variant="pills"
                >
                  <Tab eventKey="users" title={`👥 Users (${data.length})`}>
                    {/* Action Bar */}
                    <Row className="mt-4 mb-4">
                      <Col sm={6} className="d-flex align-items-center">
                        <TextField
                          size="small"
                          variant="outlined"
                          placeholder="Search for user"
                          value={searchTerm}
                          onChange={handleSearchChange}
                          slotProps={{
                            input: {
                              startAdornment: <SearchIcon />,
                            },
                          }}
                        />
                      </Col>
                      <Col sm={6} className="d-flex align-items-center justify-content-end">
                        <div className="me-3">
                          <Multiselect
                            options={availableUsers}
                            selectedValues={selectedUsers}
                            onSelect={(selectedList) => setSelectedUsers(selectedList)}
                            onRemove={(selectedList) => setSelectedUsers(selectedList)}
                            displayValue="label"
                            placeholder="Search and add users..."
                            closeOnSelect={false}
                            showCheckbox={true}
                            style={{
                              option: {
                                backgroundColor: '#f5f7fa', // very light gray blue
                                color: '#2c3e50',           // dark slate blue
                              },
                              selectedOption: {
                                backgroundColor: '#7fb3d5', // soft blue
                                color: 'white',
                              },
                              chips: {
                                backgroundColor: '#85cbfdff', // light blue-gray
                                color: 'black',           // medium blue text
                                fontWeight: '500'
                              },
                              searchBox: {
                                border: '1px solid #ccd6e0',
                                borderRadius: '6px',
                                padding: '8px',
                              },
                              optionContainer: {
                                maxHeight: '180px',
                                overflowY: 'auto',
                              },
                            }}
                            className="flex-grow-1 me-3"
                          />

                        </div>

                        <Button
                          onClick={handleAddUsers}
                          disabled={addingUsers}
                          variant="primary"

                        >
                          {addingUsers ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                className="me-2"
                              />
                              Adding...
                            </>
                          ) : (
                            "+ Add Users"
                          )}

                        </Button>
                      </Col>

                    </Row>


                    <div className="mt-3">
                      <UserGroupTable
                        data={filteredData}
                        onUserDeleted={fetchUsers}
                        userGroupId={id}
                        cookies={cookies}
                        showToast={showToast}
                      />
                    </div>
                  </Tab>

                  <Tab eventKey="courses" title={`📚 Courses (${courseNames.length})`}>
                    <div className="mt-3">
                      <Row className="mb-3">
                        <Col sm={6}>
                          <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search for course"
                            value={courseSearchTerm}
                            onChange={handleCourseSearchChange}
                            slotProps={{
                              input: {
                                startAdornment: <SearchIcon />,
                              },
                            }}
                          />
                        </Col>
                      </Row>
                      <CoursesGroupTable
                        data={filteredCourses}



                      />
                    </div>
                  </Tab>

                  {hasFeature(FEATURES.LIVE_CLASS) && (
                    <Tab eventKey="classes" title={`🎥 Live Classes (${classes.length})`}>
                      <div className="d-flex flex-column">
                        <Row className="mt-2 mb-3">
                            <Col sm={6} className="d-flex align-items-center">
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search for live class"
          value={liveClassSearchTerm}
          onChange={handleLiveClassSearchChange}
          slotProps={{
            input: {
              startAdornment: <SearchIcon />,
            },
          }}
        />
      </Col>
                          <Col className="d-flex justify-content-end">
                            {hasFeature(FEATURES.LIVE_CLASS) && (
                              <Button
                                onClick={() => setShowMeetingModel(true)}
                                variant="outline-primary"
                              >
                                📅 Schedule Live Class
                              </Button>



                            )}
                          </Col>
                        </Row>

                        <div className="mt-3">
                          <LiveClassesGroupTable
  classes={
    liveClassSearchTerm
      ? classes.filter((liveClass) =>
          liveClass?.title?.toLowerCase().includes(liveClassSearchTerm.toLowerCase())
        )
      : classes
  }
                            existingUsers={existingUsers}
                            fetchAllLiveClasses={fetchAllLiveClasses}
                          />
                        </div>
                      </div>
                    </Tab>
                  )}
                </Tabs>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Create Meeting Modal */}
      <CreateMeetingModal
        show={showMeetingModel}
        onHide={() => setShowMeetingModel(false)}
        userGroupId={id}
        existingUsers={existingUsers}
        cookies={cookies}
        showToast={showToast}
        onMeetingCreated={fetchAllLiveClasses}
      />

      {/* Update User Group Modal */}
      <CommonDialog
        open={updateUserGroupModal}
        headerText="Update Training Program"
        handleDialogCLose={updateUserModalCloseHandler}
        handleSaveOperation={updateUserGroupData}
        content={
          <UpdateUserGroup
            userGroup={clickedUserGroup}
            handleChangeForUpdateUser={handleChangeForUpdateUser}
          />
        }
        isSaveBtnLoading={updateOperationLoading}
        saveBtnText="Update"
      />
    </Fragment>
  );
};

export default Users;